import React, { FC } from 'react'
import { Grid, Stack } from '@mui/material'
import moment from 'moment'

import { FragmentType, graphql, useFragment } from '@/generated/gql'
import ServiceSelectionGraphql from './ServiceSelectionGraphql'
import SubsidyAndBalance from '../SubsidyAndBalance'
import OrderTimeLeft from './OrderTimeLeft'
import TimeslotSelectionGraphql from '../TimeslotSelectionGraphql'
import LocationSelectionGraphql from '../LocationSelectionGraphql'

const canteenMenuNavBarCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuNavBar_CurrentMenuDataFragment on CCMealService {
    ...CanteenTimeslotSelection_MenuDataFragment
    ...CanteenSubsidyAndBalance_CurrentMenuDataFragment
    timeslots {
      id
      startTime
    }
  }
`)

const canteenMenuNavBarUserDataFragment = graphql(`
  fragment CanteenMenuNavBar_UserDataFragment on User {
    account {
      userInterfaceSettings {
        hideCCPrices
      }
      locationsPaged(filters: { isCCActive: true }) {
        rows {
          id
        }
      }
    }
    ...CanteenServiceSelection_UserDataFragment
    ...CanteenLocationSelection_UserDataFragment
    ...CanteenSubsidyAndBalance_UserDataFragment
  }
`)

type Props = {
  currency: string
  date: string
  meal: string
  arrivalTime?: string | null
  menuClosed: boolean
  openingTime?: string
  orderTimeRemaining?: moment.Duration
  menuData?: FragmentType<typeof canteenMenuNavBarCurrentMenuDataFragment> | null
  userData: FragmentType<typeof canteenMenuNavBarUserDataFragment>
  onChangeTimeslot: (timeslot: string) => void
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
}

const NavBar: FC<Props> = ({
  date,
  meal,
  arrivalTime,
  menuClosed,
  openingTime,
  currency,
  orderTimeRemaining,
  menuData: menuDataProp,
  userData: userDataProp,
  onChangeAccountLocation,
  onChangeSelectedService,
  onChangeTimeslot,
}) => {
  const menuData = useFragment(canteenMenuNavBarCurrentMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenMenuNavBarUserDataFragment, userDataProp)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const locations = userData.account?.locationsPaged.rows || []

  return (
    <Grid
      wrap="wrap"
      padding={1}
      alignContent="center"
      alignItems="center"
      container
      spacing={1}
      width="100%"
    >
      {locations.length > 1 && (
        <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
          <LocationSelectionGraphql onChange={onChangeAccountLocation} userData={userData} />
        </Grid>
      )}
      <Grid item xs={12} sm={hidePrices ? 12 : 6} md={6} lg={3} xl={3}>
        <ServiceSelectionGraphql
          onChangeSelectedService={onChangeSelectedService}
          selectedDate={date}
          selectedMeal={meal}
          serviceData={userData}
        />
      </Grid>
      {arrivalTime && !menuClosed ? (
        <Grid item xs={12} sm={6} md={6} lg={2} xl={2}>
          <TimeslotSelectionGraphql
            onChangeTimeslot={onChangeTimeslot}
            selectedTimeslot={arrivalTime}
            timeslots={menuData}
          />
        </Grid>
      ) : null}
      {arrivalTime && (
        <Grid item xs={hidePrices ? 12 : 6} sm={6} md={hidePrices ? 6 : 3} lg={2} xl={1}>
          <OrderTimeLeft
            menuClosed={menuClosed}
            openingTime={menuData ? openingTime : null}
            orderTimeRemaining={menuData ? orderTimeRemaining : null}
          />
        </Grid>
      )}
      {menuData && !hidePrices ? (
        <Grid
          display="flex"
          flexDirection="row-reverse"
          marginLeft="auto"
          minWidth="200px"
          width="100%"
          item
          xs={6}
          sm={6}
          md={3}
          lg={2}
          xl={2}
        >
          <Stack width="100%" maxWidth={{ xl: '200px' }}>
            <SubsidyAndBalance currency={currency} menuData={menuData} userData={userData} />
          </Stack>
        </Grid>
      ) : null}
    </Grid>
  )
}

export default NavBar
